import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { useUISettings } from './useUISettings';
import { useAccount } from './useAccount';

const DEFAULT_SETTINGS = {
  enabled: true,
  contactLanguage: 'en',
  agentLanguage: 'zh_CN',
  sendTranslation: true,
  sendPreview: false,
  receiveTranslation: true,
};

const settingsByConversationId = new Map();

const toClientSettings = (serverSettings = {}, fallbackAgentLanguage = 'zh_CN') => ({
  enabled: serverSettings.enabled ?? DEFAULT_SETTINGS.enabled,
  contactLanguage: serverSettings.contact_language ?? DEFAULT_SETTINGS.contactLanguage,
  agentLanguage:
    serverSettings.agent_language ?? fallbackAgentLanguage ?? DEFAULT_SETTINGS.agentLanguage,
  sendTranslation: serverSettings.send_translation ?? DEFAULT_SETTINGS.sendTranslation,
  sendPreview: serverSettings.send_preview ?? DEFAULT_SETTINGS.sendPreview,
  receiveTranslation:
    serverSettings.receive_translation ?? DEFAULT_SETTINGS.receiveTranslation,
});

const toServerSettings = clientSettings => ({
  enabled: clientSettings.enabled,
  contact_language: clientSettings.contactLanguage,
  agent_language: clientSettings.agentLanguage,
  send_translation: clientSettings.sendTranslation,
  send_preview: clientSettings.sendPreview,
  receive_translation: clientSettings.receiveTranslation,
});

const getOrCreateSettingsRef = (conversationId, initialSettings) => {
  if (!settingsByConversationId.has(conversationId)) {
    settingsByConversationId.set(conversationId, ref({ ...initialSettings }));
  }
  return settingsByConversationId.get(conversationId);
};

const readServerSettings = (conversation, fallbackAgentLanguage) => {
  const stored = conversation?.additional_attributes?.translation_settings;
  return toClientSettings(stored, fallbackAgentLanguage);
};

const migrateLocalStorageToServer = async (store, conversationId, fallbackAgentLanguage) => {
  const legacyStore = LocalStorage.get(
    LOCAL_STORAGE_KEYS.CONVERSATION_TRANSLATION_SETTINGS
  );
  const legacySettings = legacyStore?.[conversationId];
  if (!legacySettings) return;

  try {
    await store.dispatch('saveConversationTranslationSettings', {
      conversationId,
      translationSettings: toServerSettings({
        ...DEFAULT_SETTINGS,
        agentLanguage: fallbackAgentLanguage,
        ...legacySettings,
      }),
    });
    delete legacyStore[conversationId];
    LocalStorage.set(LOCAL_STORAGE_KEYS.CONVERSATION_TRANSLATION_SETTINGS, legacyStore);
  } catch {
    // Keep legacy settings if migration fails; user can retry on next change.
  }
};

export function useConversationTranslation(conversationIdRef) {
  const store = useStore();
  const { uiSettings } = useUISettings();
  const { currentAccount } = useAccount();

  const fallbackAgentLanguage = computed(
    () =>
      uiSettings.value?.locale || currentAccount.value?.locale || 'zh_CN'
  );

  const selectedConversation = computed(() => {
    const conversationId = conversationIdRef.value;
    if (!conversationId) return null;

    const selected = store.getters.getSelectedChat;
    if (selected?.id === conversationId) return selected;

    return store.state.conversations.allConversations.find(
      c => c.id === conversationId
    );
  });

  const hydrateSettings = async conversationId => {
    if (!conversationId) return;

    const serverSettings = readServerSettings(
      selectedConversation.value,
      fallbackAgentLanguage.value
    );
    const settingsRef = getOrCreateSettingsRef(conversationId, serverSettings);
    settingsRef.value = serverSettings;

    const hasServerSettings =
      selectedConversation.value?.additional_attributes?.translation_settings;
    if (!hasServerSettings) {
      await migrateLocalStorageToServer(
        store,
        conversationId,
        fallbackAgentLanguage.value
      );
    }
  };

  watch(
    [conversationIdRef, selectedConversation, fallbackAgentLanguage],
    ([conversationId, conversation]) => {
      if (!conversationId || !conversation) return;

      const serverSettings = readServerSettings(
        conversation,
        fallbackAgentLanguage.value
      );
      const settingsRef = getOrCreateSettingsRef(conversationId, serverSettings);
      settingsRef.value = serverSettings;
    },
    { immediate: true, deep: true }
  );

  watch(conversationIdRef, conversationId => {
    hydrateSettings(conversationId);
  }, { immediate: true });

  const settings = computed({
    get() {
      const conversationId = conversationIdRef.value;
      if (!conversationId) return { ...DEFAULT_SETTINGS };

      return getOrCreateSettingsRef(
        conversationId,
        readServerSettings(
          selectedConversation.value,
          fallbackAgentLanguage.value
        )
      ).value;
    },
    set(newSettings) {
      const conversationId = conversationIdRef.value;
      if (!conversationId) return;

      getOrCreateSettingsRef(conversationId, newSettings).value = newSettings;
    },
  });

  const isActive = computed(
    () => settings.value.enabled && settings.value.sendTranslation
  );

  const shouldAutoTranslateIncoming = computed(
    () => settings.value.enabled && settings.value.receiveTranslation
  );

  const incomingTargetLanguage = computed(() => settings.value.agentLanguage);

  const outgoingTargetLanguage = computed(
    () => settings.value.contactLanguage
  );

  const updateSettings = async partialSettings => {
    const conversationId = conversationIdRef.value;
    if (!conversationId) return;

    const settingsRef = getOrCreateSettingsRef(conversationId, settings.value);
    const nextSettings = {
      ...settingsRef.value,
      ...partialSettings,
    };
    settingsRef.value = nextSettings;

    try {
      await store.dispatch('saveConversationTranslationSettings', {
        conversationId,
        translationSettings: toServerSettings(nextSettings),
      });
    } catch {
      settingsRef.value = readServerSettings(
        selectedConversation.value,
        fallbackAgentLanguage.value
      );
    }
  };

  return {
    settings,
    isActive,
    shouldAutoTranslateIncoming,
    incomingTargetLanguage,
    outgoingTargetLanguage,
    updateSettings,
  };
}
